import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Stack,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Work,
  LocationOn,
  Close,
  Send,
  Verified,
  CurrencyRupee,
  WorkHistory,
  AutoAwesome,
  CloudUpload,
  Person,
  Email,
  Phone,
  Language,
  CheckCircle,
  InsertDriveFile,
  Close as CloseIcon,
} from "@mui/icons-material";
import { submitJobApplication, uploadCandidateCV } from "../../Action/api";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  experience: "",
  linkedin: "",
  portfolio: "",
  coverLetter: "",
};

const ApplyDialog = ({ open, job, onClose, onSuccess }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadStep, setUploadStep] = useState(""); // "uploading" | "submitting" | ""
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successData, setSuccessData] = useState(null); // { profileId, jobTitle }

  // Reset form every time the dialog opens (or a different job is selected)
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setResumeFile(null);
      setUploadStep("");
      setSubmitError("");
      setSuccessData(null);
    }
  }, [open, job?.id]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setResumeFile(file);
  };

  const handleRemoveFile = (e) => {
    e.preventDefault();
    setResumeFile(null);
  };

  const handleClose = () => {
    setSubmitError("");
    setSuccessData(null);
    onClose();
  };

  const handleSuccessDone = () => {
    setSuccessData(null);
    onSuccess();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");

    // Step 1 — submit application, get profileId
    setUploadStep("submitting");
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      experience: form.experience,
      linkedin: form.linkedin,
      portfolio: form.portfolio,
      coverLetter: form.coverLetter,
      jobId: job?.id,
      jobTitle: job?.title,
      department: job?.department,
      location: job?.location,
      jobType: job?.type,
      workMode: job?.mode,
      salary: job?.salary,
      resumeFileExt: resumeFile ? resumeFile.name.split(".").pop() : null,
      appliedAt: new Date().toISOString(),
      status: "Applied",
    };
    const result = await submitJobApplication(payload);
    if (!result.success) {
      setSubmitError(result.message || "Something went wrong. Please try again.");
      setSubmitting(false);
      setUploadStep("");
      return;
    }

    // Step 2 — upload CV using profileId as filename (if file selected)
    if (resumeFile && result.profileId) {
      setUploadStep("uploading");
      const uploadResult = await uploadCandidateCV(resumeFile, result.profileId);
      if (!uploadResult.success) {
        // Application was submitted; show warning but still treat as success
        console.warn("CV upload failed after successful application:", uploadResult.message);
      }
    }

    setSubmitting(false);
    setUploadStep("");
    setForm(EMPTY_FORM);
    setResumeFile(null);
    setSuccessData({ profileId: result.profileId, jobTitle: job?.title });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* ── Gradient Header ── */}
      <Box
        sx={{
          background: job?.color
            ? `linear-gradient(135deg, ${job.color} 0%, #6366f1 100%)`
            : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          p: 3,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", top: -20, right: -20, width: 130, height: 130, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)" }} />
        <Box sx={{ position: "absolute", bottom: -30, right: 80, width: 80, height: 80, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)" }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Work sx={{ color: "white", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  mb: 0.3,
                }}
              >
                Apply for Position
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, color: "white", lineHeight: 1.2, mb: 1 }}>
                {job?.title}
              </Typography>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                <Chip
                  label={job?.department}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: "0.72rem", fontWeight: 700, border: "none", height: 22 }}
                />
                {job?.location && (
                  <Chip
                    icon={<LocationOn sx={{ color: "rgba(255,255,255,0.85) !important", fontSize: "13px !important" }} />}
                    label={job.location}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontSize: "0.72rem", height: 22, border: "none" }}
                  />
                )}
                {job?.salary && (
                  <Chip
                    icon={<CurrencyRupee sx={{ color: "rgba(255,255,255,0.85) !important", fontSize: "13px !important" }} />}
                    label={job.salary}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontSize: "0.72rem", height: 22, border: "none" }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" }, flexShrink: 0 }}
          >
            <Close />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Trust bar ── */}
      <Box sx={{ px: 3, py: 1.2, bgcolor: "#f0fdf4", borderBottom: "1px solid #dcfce7" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Verified sx={{ fontSize: 15, color: "#16a34a" }} />
          <Typography sx={{ fontSize: "0.77rem", color: "#166534", fontWeight: 500 }}>
            Takes only <strong>2 minutes</strong> · Your information is secure and will not be shared without consent.
          </Typography>
        </Stack>
      </Box>

      {/* ── Success Screen ── */}
      {successData ? (
        <>
          <DialogContent sx={{ p: 4, textAlign: "center" }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
              }}
            >
              <CheckCircle sx={{ fontSize: 40, color: "#16a34a" }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a", mb: 0.5 }}>
              Application Submitted!
            </Typography>
            <Typography sx={{ fontSize: "0.88rem", color: "#64748b", mb: 3 }}>
              Your application for <strong>{successData.jobTitle}</strong> has been received.
            </Typography>

            {/* Reference ID box */}
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                border: "2px dashed #c7d2fe",
                borderRadius: 3,
                px: 4,
                py: 2,
                bgcolor: "#f5f3ff",
                mx: "auto",
                mb: 3,
              }}
            >
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: 1.5, mb: 0.5 }}>
                Application Reference ID
              </Typography>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "1.9rem",
                  fontWeight: 900,
                  color: "#4f46e5",
                  letterSpacing: 4,
                }}
              >
                {successData.profileId}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mt: 0.5 }}>
                Save this ID to track your application status
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>
              We'll reach out to you soon. Good luck!
            </Typography>
          </DialogContent>
          <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #f1f5f9", bgcolor: "#fafbfc", display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={handleSuccessDone}
              sx={{
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 2,
                px: 5,
                py: 1.3,
                boxShadow: "0 4px 15px rgba(99,102,241,0.35)",
                "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" },
              }}
            >
              Done
            </Button>
          </Box>
        </>
      ) : (
      <>

      <DialogContent sx={{ p: 3, pt: "24px !important" }}>
        <Stack spacing={3.5}>

          {/* ── Personal Information ── */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.2} mb={2}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Person sx={{ fontSize: 17, color: "#7c3aed" }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.92rem" }}>Personal Information</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full Name *"
                  fullWidth
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address *"
                  type="email"
                  fullWidth
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number *"
                  fullWidth
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Years of Experience"
                  select
                  fullWidth
                  value={form.experience}
                  onChange={(e) => setForm({ ...form, experience: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><WorkHistory sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {["Fresher (0–1 yr)", "1–2 Years", "2–4 Years", "4–6 Years", "6–10 Years", "10+ Years"].map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ borderColor: "#f1f5f9" }} />

          {/* ── Professional Links ── */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.2} mb={2}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Language sx={{ fontSize: 17, color: "#0284c7" }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.92rem" }}>Professional Links</Typography>
              <Chip label="Optional" size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#f1f5f9", color: "#64748b" }} />
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="LinkedIn Profile"
                  fullWidth
                  value={form.linkedin}
                  onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/in/you"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: "0.7rem", color: "#0a66c2", fontWeight: 900, lineHeight: 1, border: "1.5px solid #0a66c2", borderRadius: "3px", px: 0.4, py: 0.2 }}>in</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Portfolio / GitHub URL"
                  fullWidth
                  value={form.portfolio}
                  onChange={(e) => setForm({ ...form, portfolio: e.target.value })}
                  placeholder="https://github.com/yourname"
                  InputProps={{ startAdornment: <InputAdornment position="start"><Language sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ borderColor: "#f1f5f9" }} />

          {/* ── Cover Letter ── */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.2} mb={2}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AutoAwesome sx={{ fontSize: 17, color: "#d97706" }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.92rem" }}>Cover Letter</Typography>
              <Chip label="Optional" size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#f1f5f9", color: "#64748b" }} />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                onClick={() => {
                  const name = form.name?.trim() || "I";
                  const title = job?.title || "this position";
                  const dept = job?.department || "your team";
                  const company = "OruClick";
                  const template =
`Dear Hiring Team,

I am excited to apply for the ${title} role at ${company}. With my background in ${dept}, I am confident in my ability to contribute meaningfully to your team.

Throughout my career, I have developed strong skills that align well with this position. I am particularly drawn to ${company}'s mission and believe this role offers an excellent opportunity to apply my expertise while continuing to grow professionally.

I thrive in collaborative environments, take ownership of my work, and am passionate about delivering high-quality results. I would be thrilled to bring my skills and enthusiasm to ${company}.

Thank you for considering my application. I look forward to the opportunity to discuss how I can contribute to your team.

Warm regards,
${name}`;
                  setForm((prev) => ({ ...prev, coverLetter: template }));
                }}
                sx={{
                  fontSize: "0.72rem",
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#d97706",
                  bgcolor: "#fef3c7",
                  borderRadius: 1.5,
                  px: 1.2,
                  py: 0.4,
                  "&:hover": { bgcolor: "#fde68a" },
                }}
              >
                Use Template
              </Button>
            </Stack>
            <TextField
              multiline
              rows={4}
              fullWidth
              value={form.coverLetter}
              onChange={(e) => setForm({ ...form, coverLetter: e.target.value })}
              placeholder="Tell us why you'd be a great fit at OruClick — your passion, achievements, and what excites you about this role…"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mt: 0.5, textAlign: "right" }}>
              {form.coverLetter.length} / 1000 characters
            </Typography>
          </Box>

          {/* ── Resume Upload ── */}
          {resumeFile ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "2px solid #86efac",
                borderRadius: 3,
                p: 2,
                bgcolor: "#f0fdf4",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <InsertDriveFile sx={{ fontSize: 20, color: "#16a34a" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#166534", fontSize: "0.87rem", wordBreak: "break-all" }}>
                    {resumeFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#4ade80" }}>
                    {(resumeFile.size / 1024).toFixed(1)} KB · Ready to upload
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircle sx={{ fontSize: 20, color: "#16a34a" }} />
                <IconButton size="small" onClick={handleRemoveFile} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Box>
          ) : (
            <Box
              component="label"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed #c7d2fe",
                borderRadius: 3,
                p: 3,
                bgcolor: "#f5f3ff",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { borderColor: "#6366f1", bgcolor: "#ede9fe" },
              }}
            >
              <CloudUpload sx={{ fontSize: 34, color: "#6366f1", mb: 0.5 }} />
              <Typography sx={{ fontWeight: 700, color: "#4f46e5", fontSize: "0.9rem" }}>
                Click to Upload Resume
              </Typography>
              <Typography sx={{ fontSize: "0.73rem", color: "#64748b", mt: 0.3 }}>
                PDF, DOC, DOCX · Max 5 MB
              </Typography>
              <input type="file" hidden accept=".pdf,.doc,.docx" onChange={handleFileChange} />
            </Box>
          )}
          {submitError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      {/* ── Footer Actions ── */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid #f1f5f9",
          bgcolor: "#fafbfc",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: "0.73rem", color: "#94a3b8" }}>* Required fields</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            onClick={handleClose}
            sx={{ color: "#64748b", textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            endIcon={submitting ? null : <Send />}
            disabled={!form.name || !form.email || !form.phone || submitting}
            onClick={handleSubmit}
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              py: 1.3,
              minWidth: 190,
              boxShadow: "0 4px 15px rgba(99,102,241,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 6px 20px rgba(99,102,241,0.45)",
              },
              "&.Mui-disabled": { opacity: 0.5, boxShadow: "none" },
            }}
          >
            {submitting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "white" }} />
                {uploadStep === "uploading" ? "Uploading CV…" : "Submitting…"}
              </Box>
            ) : "Submit Application"}
          </Button>
        </Stack>
      </Box>
      </>
      )}
    </Dialog>
  );
};

export default ApplyDialog;
